import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { AlertCircle, Clock } from 'lucide-react';
import { useOperationalIndicators } from '@/hooks/use-active-theme';

// Cluster urgenze in header — firma del tema Calvo (§5/§6 SPECIFICHE-DESIGN):
// due pill "N scaduti" (danger) e "N oggi" (warning) sempre visibili accanto
// alle azioni. Alimentato da DATI REALI del backend (/api/tasks con dueDate +
// /api/meetings/upcoming), non da conteggi finti. Visibile solo sotto Calvo e
// solo quando c'è almeno un elemento urgente. Cliccabile -> /tasks o /calendar.

interface TaskLite {
  dueDate?: string | null;
  completed?: boolean | null;
  status?: string | null;
  isCalendarEvent?: boolean | null;
}
interface MeetingLite {
  startDateTime?: string | null;
  startTime?: string | null;
  start?: string | null;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function CalvoUrgencyCluster() {
  const isCalvo = useOperationalIndicators();
  const [, navigate] = useLocation();

  // Gli hook vanno chiamati sempre (regola React): il gate isCalvo filtra il render.
  const { data: tasksRaw } = useQuery<any>({
    queryKey: ['/api/tasks?status=pending&limit=200'],
    enabled: isCalvo,
    staleTime: 60_000,
  });
  const { data: meetingsRaw } = useQuery<any>({
    queryKey: ['/api/meetings/upcoming'],
    enabled: isCalvo,
    staleTime: 60_000,
  });

  if (!isCalvo) return null;

  const tasks: TaskLite[] = Array.isArray(tasksRaw) ? tasksRaw : (tasksRaw?.tasks ?? tasksRaw?.data ?? []);
  const meetings: MeetingLite[] = Array.isArray(meetingsRaw) ? meetingsRaw : (meetingsRaw?.meetings ?? meetingsRaw?.data ?? []);

  const now = new Date();
  let overdue = 0;
  let todayTasks = 0;
  let todayEvents = 0;

  for (const t of tasks) {
    if (t.completed || t.status === 'completed' || t.status === 'done') continue;
    const raw = t.dueDate;
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    if (isSameDay(d, now)) {
      if (t.isCalendarEvent === true) todayEvents++;
      else todayTasks++;
    }
    else if (d.getTime() < now.getTime()) overdue++;
  }

  for (const m of meetings) {
    const raw = m.startDateTime ?? m.startTime ?? m.start;
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    if (isSameDay(d, now)) todayEvents++;
  }

  if (overdue === 0 && todayTasks === 0 && todayEvents === 0) return null;

  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      {overdue > 0 && (
        <button
          type="button"
          onClick={() => navigate('/tasks')}
          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-[#f6d3d3] bg-calvo-danger-soft px-2.5 text-xs font-semibold text-calvo-danger-text tabular-nums"
          title="Task scaduti — apri Task"
        >
          <span className="relative flex h-[7px] w-[7px]">
            <span className="absolute inline-flex h-full w-full animate-calvo-pulse rounded-full bg-calvo-danger opacity-80" />
            <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-calvo-danger" />
          </span>
          {overdue} {overdue === 1 ? 'scaduto' : 'scaduti'}
        </button>
      )}
      {todayTasks > 0 && (
        <button
          type="button"
          onClick={() => navigate('/tasks')}
          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-[#f0dfb0] bg-calvo-warning-soft px-2.5 text-xs font-semibold text-calvo-warning-text tabular-nums"
          title="Task in scadenza oggi — apri Task"
        >
          <Clock className="h-3.5 w-3.5" />
          {todayTasks} {todayTasks === 1 ? 'task oggi' : 'task oggi'}
        </button>
      )}
      {todayEvents > 0 && (
        <button
          type="button"
          onClick={() => navigate('/calendar')}
          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-[#f0dfb0] bg-calvo-warning-soft px-2.5 text-xs font-semibold text-calvo-warning-text tabular-nums"
          title="Eventi oggi — apri Calendario"
        >
          <Clock className="h-3.5 w-3.5" />
          {todayEvents} {todayEvents === 1 ? 'evento oggi' : 'eventi oggi'}
        </button>
      )}
    </div>
  );
}
